<script>
import { pathOr, endsWith, pipe } from 'ramda'
import render from 'posthtml-render'

import { propValidator, PROP_TYPES } from '@/utils/validators'

import AVisibility from 'shared/AVisibility'
import LazyHydrate from '@/utils/helpers/vue-lazy-hydration/LazyHydrate'
import AEbookWithTransition from 'shared/AEbookWithTransition'
import ATerm from 'shared/ATerm'
import ATableInsideArticle, {
  TABLE_SHORTCODE,
  TABLE_APOSTROPHE,
  TABLE_DOUBLE_QUOTES
} from './TableInsideArticle'
import { REFS } from 'enums/external-refs'
import { EBOOK_MEDIA_QUERY_MAP } from 'shared/AEbook/enums'
import { MODAL } from 'modals/AModalWrapper'
import { CUSTOM_HTML_TAG, HTML_TAG } from '@/utils/helpers/processHtml/enums'
import { isElementPureText } from '@/utils/helpers/processHtml/prettifyHtml'
import { processHtml } from '@/utils/helpers/processHtml'
import { decodeHTML } from 'entities'
import { parseHtml } from '@/utils/helpers/processHtml/parseHtml'
import { parseUrl, removeAmpSuffix } from '@fmpedia/helpers'
import { MEDIA_ID_DATA_ATTRIBUTE } from '@fmpedia/enums'
import {
  checkIfParsedElementIsTerm,
  getTermIdFromParsedTerm
} from '@/utils/helpers/processHtml/processTerms'
import { SRCSET_LOCATION } from 'enums/images'

const COLON = ':'
const INITIAL_EBOOK_POSITION = 3

const ARTICLE_LINK_CLASS_NAME = 'article-link'

const FIRST_FIGURE_WITH_IMG_SRC_GROUP_REGEX = new RegExp(
  '^<figure[^>]*>.*?<img[^>]+src=["\'](.*?)["\'].*?<\\/figure>',
  'gsi'
)

function wrapIntoLazyHydrate(component) {
  return {
    tag: 'a-lazy-hydrate',
    props: {
      'when-visible': true
    },
    content: [component]
  }
}

function renderWithLazyHydrate(h, component) {
  return h(
    LazyHydrate,
    {
      props: { 'when-visible': true }
    },
    [component]
  )
}

export default {
  name: 'ArticleBody',
  components: {
    AVisibility,
    AEbookWithTransition,
    ATerm,
    ATableInsideArticle
  },
  props: {
    body: propValidator([PROP_TYPES.STRING]),
    ebook: propValidator([PROP_TYPES.OBJECT], false),
    terms: propValidator([PROP_TYPES.ARRAY]),
    isSponsored: propValidator([PROP_TYPES.BOOLEAN], false, false),
    isMigrated: propValidator([PROP_TYPES.BOOLEAN]),
    tableData: propValidator([PROP_TYPES.STRING], false),
    image: propValidator([PROP_TYPES.OBJECT], false),
    articleTextSize: propValidator([PROP_TYPES.STRING], false)
  },
  data() {
    return {
      processedBody: null,
      articleParagraphCount: 0,
      firstImageMediaId: null,
      isFirstImageOptimized: false
    }
  },
  computed: {
    srcsetLocation() {
      return this.$helper.isAmpPage(this.$route.name)
        ? SRCSET_LOCATION.AMP_ARTICLE_PAGE
        : SRCSET_LOCATION.ARTICLE_PAGE
    }
  },
  methods: {
    processBody() {
      const bodyWithTableInsideArticle = this.setTableInsideArticle(this.body)

      return this.removeDuplicatedTopImageFromHtml(
        processHtml(bodyWithTableInsideArticle, {
          allowScripts: true,
          handleTwitterCards: true,
          imageCustomComponent: CUSTOM_HTML_TAG.A_ENLARGEABLE_IMAGE,
          srcsetLocation: this.srcsetLocation,
          imageModifierFn: this.firstImageMediaId
            ? this.enableViewportOptimization.bind(this)
            : null,
          addImageSizeClass: true
        })
      )
    },
    getArticleParagraphCount() {
      const processedHtmlParsed = parseHtml(this.processedBody)

      return processedHtmlParsed.reduce((paragraphCount, element) => {
        if (element.tag === HTML_TAG.PARAGRAPH) {
          return ++paragraphCount
        }

        return paragraphCount
      }, 0)
    },
    /**
     * According to the client requirements, the FeaturedImage of the
     * Education article was migrated as the first image inside article's
     * body. This leads to situations when two identical images might be
     * rendered next to each other. In case if the duplicated image
     * (and ONLY in this case) is located at the very top of the article
     * do we remove it from the article body.
     */
    removeDuplicatedTopImageFromHtml(html) {
      if (!this.image || !this.isMigrated || !html) return html

      let firstImageSrc = this.$helper.getFirstRegexGroups(
        FIRST_FIGURE_WITH_IMG_SRC_GROUP_REGEX,
        html
      )[0]

      if (!firstImageSrc) return html

      /**
       * BE appends 'https' protocol to the FeaturedImage Url in case when
       * it starts with '//'. We need to do the same with the Url of the
       * image inside article before comparison
       */
      if (this.$helper.isStringStartsWith('//', firstImageSrc)) {
        firstImageSrc = `https:${firstImageSrc}`
      }

      const firstImageAddress = parseUrl(firstImageSrc).address
      const featuredImageAddress = parseUrl(this.image.url).address

      if (firstImageAddress === featuredImageAddress) {
        return html.replace(FIRST_FIGURE_WITH_IMG_SRC_GROUP_REGEX, '')
      }

      return html
    },
    enableViewportOptimization({ mediaId, parsedImage }) {
      if (
        this.isFirstImageOptimized ||
        this.image ||
        !mediaId ||
        mediaId !== this.firstImageMediaId
      ) {
        return parsedImage
      }

      this.isFirstImageOptimized = true
      parsedImage.attrs['viewport-optimization'] = true

      return parsedImage
    },
    parseBody() {
      return parseHtml(this.processedBody.replace(/&nbsp;/gi, ' '))
    },
    incrementParagraphCounter(element) {
      if (element.tag === HTML_TAG.PARAGRAPH) {
        this.$options.paragraphCounter += 1
      }
    },
    createEbookNode(h) {
      this.$options.isEbookAdded = true

      return h(AEbookWithTransition, {
        props: {
          ebookData: this.ebook
        },
        attrs: {
          mediaQueryMap: EBOOK_MEDIA_QUERY_MAP.MOBILE_EBOOK_MAP
        }
      })
    },
    createAVisibilityWithEbook(h) {
      this.$options.isEbookAdded = true

      return renderWithLazyHydrate(
        h,
        h(
          AVisibility,
          {
            props: { show: true, on: [this.$breakpoint.mobile] }
          },
          [this.createEbookNode(h)]
        )
      )
    },
    setTableInsideArticle(body) {
      if (!this.tableData || !body.includes(TABLE_SHORTCODE)) return body

      const tableData = this.tableData
        .replace(/'/gi, TABLE_APOSTROPHE)
        .replace(/"/gi, TABLE_DOUBLE_QUOTES)

      /**
       * Empty space counts as a separate root node if passed as a slot to the component
       * To avoid that, we concatenate components without spaces
       */

      let tableTag

      if (this.$helper.isAmpPage(this.$route.name)) {
        tableTag = `<a class="text-underline" href="${
          this.$env.IL_DOMAIN_URL
        }${removeAmpSuffix(
          this.$route.path
        )}">To see our comprehensive Table click here and be directed to the full version of the article.</a>`
      } else {
        tableTag =
          `<a-lazy-hydrate when-visible>` +
          `<a-table-inside-article tableData="${tableData}" />` +
          `</a-lazy-hydrate>`
      }

      return body.replace(TABLE_SHORTCODE, tableTag)
    },
    isParagraphEndsWithColon(element) {
      const contentLength = pathOr(0, ['content', 'length'], element)
      if (!contentLength) return false

      const lastChild = element.content[contentLength - 1]
      const isTypeString = typeof lastChild === 'string'

      return isTypeString && endsWith(COLON, lastChild)
    },
    addInterruptiveElementInsideBody({ h, element, children }) {
      if (
        !this.$options.isEbookAdded &&
        this.ebook &&
        this.$options.paragraphCounter === this.$options.ebookPosition
      ) {
        if (this.isParagraphEndsWithColon(element)) {
          this.$options.ebookPosition += 1
          return children
        }
        return [...children, this.createAVisibilityWithEbook(h)]
      } else {
        return children
      }
    },
    addInterruptiveElementsAtTheEnd(h, children) {
      const isEbookShouldBeAdded = this.ebook && !this.$options.isEbookAdded

      return [
        ...children,
        ...(isEbookShouldBeAdded ? [this.createAVisibilityWithEbook(h)] : [])
      ]
    },
    createElementAttributes(element) {
      return {
        props: element.props,
        attrs: element.attrs,
        class: element.class,
        style: pathOr(null, ['attrs', 'style'], element),
        on: element.on
      }
    },
    isNoElement(element) {
      return element === undefined
    },
    isChildrenExist(element) {
      return element.content && !!element.content.length
    },
    getArticleLinkClass(linkClass) {
      return linkClass
        ? `${linkClass} ${ARTICLE_LINK_CLASS_NAME}`
        : ARTICLE_LINK_CLASS_NAME
    },
    addClassToLinkTag(element) {
      if ([HTML_TAG.ANCHOR, CUSTOM_HTML_TAG.A_LINK].includes(element.tag)) {
        element.attrs = element.attrs || {}
        element.attrs.class = this.getArticleLinkClass(element.attrs.class)
      }

      return element
    },
    handleFigureEvents(element) {
      if (element.tag === HTML_TAG.FIGURE) {
        const enlargeableIconEl = element.content.find(child => {
          return (
            child &&
            child.tag &&
            child.tag === CUSTOM_HTML_TAG.A_ENLARGEABLE_IMAGE
          )
        })
        if (!enlargeableIconEl) return element

        enlargeableIconEl.on = {
          enlarge: () => {
            const img = element.content.find(
              ({ tag }) => tag === CUSTOM_HTML_TAG.A_ENLARGEABLE_IMAGE
            )
            this.$emit('set-popup-image', {
              MediaId: element.attrs[MEDIA_ID_DATA_ATTRIBUTE],
              MediaType: 'Image',
              URL: img.attrs['data-src']
            })
            this.$nextTick(() => this.$helper.openModal(MODAL.IMAGE_DOWNLOAD))
          }
        }
      }

      return element
    },
    handleTerms(element) {
      if (!checkIfParsedElementIsTerm(element)) return element

      const elementId = getTermIdFromParsedTerm(element)
      const { name, slug, description } =
        this.terms.find(({ id }) => id === elementId) || {}
      const content = render(element.content)

      element.tag = 'a-term'
      element.attrs = {
        content,
        name: name,
        description: description,
        slug: slug
      }

      return wrapIntoLazyHydrate(element)
    },
    processElementNode(element) {
      pipe(
        this.handleFigureEvents,
        this.handleTerms,
        this.addClassToLinkTag
      )(element)
    },
    createVueElement(h, vNode) {
      const isParentNode = !vNode

      if (isParentNode) this.initOptions()

      const targetNode = vNode || this.parseBody()
      if (targetNode.length === 0) return []

      let children = []
      targetNode.forEach((element, index) => {
        if (this.isNoElement(element)) return

        if (isElementPureText(element)) {
          children.push(decodeHTML(element))
          return
        }

        this.processElementNode(element)

        const isLastElement = index === targetNode.length - 1

        if (isParentNode) this.incrementParagraphCounter(element)

        const attributes = this.createElementAttributes(element)

        const nestedChildren = this.isChildrenExist(element)
          ? this.createVueElement(h, element.content)
          : null

        children.push(h(element.tag, attributes, nestedChildren))

        if (isParentNode) {
          children = this.addInterruptiveElementInsideBody({
            h,
            element,
            children
          })

          if (isLastElement) {
            children = this.addInterruptiveElementsAtTheEnd(h, children)
          }
        }
      })
      return children
    },
    initOptions() {
      this.$options.ebookPosition = INITIAL_EBOOK_POSITION
      this.$options.paragraphCounter = 0
      this.$options.isEbookAdded = false
      this.$options.isFirstHeadingFounded = false
    },
    createClearfix(h) {
      return h(HTML_TAG.DIV, { class: 'clearfix', style: { marginBottom: 0 } })
    },
    getFirstImageMediaId(html) {
      try {
        const searchRegexpString = `<figure[^>]+${MEDIA_ID_DATA_ATTRIBUTE}="(?<id>[^"]+)"`
        const searchRegexp = new RegExp(searchRegexpString, 'is')
        const match = html.match(searchRegexp)

        if (!match) return null

        const {
          groups: { id }
        } = match

        return id || null
      } catch (err) {
        console.log('err: ', err)
        return null
      }
    }
  },
  created() {
    this.initOptions()
  },
  render(h) {
    this.firstImageMediaId = this.getFirstImageMediaId(this.body)
    this.processedBody = this.processBody()
    this.articleParagraphCount = this.getArticleParagraphCount()
    const body = this.createVueElement(h)

    return h(
      'article',
      {
        class: [`article-body-${this.articleTextSize}`],
        attrs: { 'data-ref': REFS.ARTICLE_BODY }
      },
      [body, this.createClearfix(h)]
    )
  }
}
</script>

<style lang="scss" scoped>
figure {
  cursor: pointer;
}
</style>
